'use client'

import { ArrowRight, Building2, CheckCircle2, Lock, Mail, Shield, Target, Zap } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'

import { AuthClient } from '@/auth/AuthClient'
import { RegisterForm } from '@/components/Auth/RegisterForm'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API_AUTH_BASE_URL } from '@/consts/baseUrls'
import { isAdult } from '@/lib/registrationAge'
import axios from '@/utils/axios'
import { FormEvent, useState } from 'react'
import { toast } from 'sonner'
import { LogoFullSizable } from 'src/components/Logo/LogoFullSizable'

export function Form() {
    const searchParams = useSearchParams()
    const isVerified = searchParams.get('verified') === '1'
    const [isLogin, setIsLogin] = useState(true)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showForgotPassword, setShowForgotPassword] = useState(false)
    const [resetEmail, setResetEmail] = useState('')
    const [isResetting, setIsResetting] = useState(false)
    const router = useRouter()

    const handleForgotPassword = async (e: FormEvent) => {
        e.preventDefault()
        if (!resetEmail.trim()) {
            toast.error('Bitte gib deine E-Mail-Adresse ein')
            return
        }
        setIsResetting(true)
        try {
            await axios.post(`${API_AUTH_BASE_URL}/password-reset/request`, {
                email: resetEmail.trim(),
            })

            toast.success('Falls ein Account mit dieser E-Mail existiert, erhältst du einen Link zum Zurücksetzen.')

            setShowForgotPassword(false)
            setResetEmail('')
        } catch (_error) {
            toast.error('Netzwerkfehler. Bitte versuche es erneut.')
        } finally {
            setIsResetting(false)
        }
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            const result = await AuthClient.loginAsUser(email, password)
            if (!result.ok) {
                if (result.error === 'Email not verified.') {
                    toast.error('Bitte bestaetige zuerst deine E-Mail.')
                    return
                }
                toast.error('E-Mail oder Passwort falsch')
                return
            }

            toast.success('Willkommen zurück!')
            router.replace('/chat')
        } catch (_error) {
            toast.error('Netzwerkfehler. Bitte versuche es erneut.')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <div className="w-full lg:w-1/2 flex justify-center overflow-y-auto p-6 sm:p-8 lg:p-12">
                <div className="w-full max-w-md my-auto">
                    {/* Mobile Logo*/}
                    <div className="text-center mb-6 ">
                        <LogoFullSizable mauto="m-auto" />
                        <h1 className="text-2xl font-bold gradient-text lg:hidden">
                            Walter - dein persönlicher Berufscoach
                        </h1>
                    </div>

                    {/* Verified Banner */}
                    {isVerified ? (
                        <div className="mb-6 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/30 dark:text-green-300">
                            <CheckCircle2 className="h-5 w-5 shrink-0" />
                            <p>Deine E-Mail wurde erfolgreich bestätigt. Du kannst dich jetzt anmelden.</p>
                        </div>
                    ) : null}

                    {/* Form Header */}
                    <div className="text-center mb-5">
                        {/* <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                            {isLogin ? 'Hey! 👋' : 'Entdecke deine Superkräfte 🚀'}
                        </h2> */}
                        <p className="text-muted-foreground">
                            {isLogin
                                ? // ? 'Melde dich schnell, einfach und kostenlos an und entdecke passende Berufe und Unternehmen'
                                  'Melde dich an und mach dort weiter, wo du zuletzt mit Walter gesprochen hast.'
                                : // : 'Erstelle deinen kostenlosen Account in Sekunden'
                                  //   'In unter einer Minute dabei - und das komplett kostenlos!'}
                                  'Kostenlos starten • Gespräche speichern • Jederzeit weitermachen'}
                        </p>
                    </div>

                    {/* {isLogin ? null : (
                        <div className="mt-1 pb-6 space-y-3">
                            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                    <Zap className="w-3.5 h-3.5 text-primary" />
                                    <span className="text-xs">Verstehe deine Stärken</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Target className="w-3.5 h-3.5 text-green-500" />
                                    <span>Entdecke passende Berufe</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-3.5 h-3.5 text-purple-500" />
                                    <span className="text-xs">Finde passende Unternehmen</span>
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* Form Card */}
                    <div className="glass-card rounded-2xl p-6 sm:p-8 shadow-card">
                        {isLogin ? (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium">
                                        E-Mail
                                    </Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="name@email.de"
                                            value={email}
                                            onChange={(inputEvent) => setEmail(inputEvent.target.value)}
                                            required
                                            className="h-12 rounded-xl bg-background/50 pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password" className="text-sm font-medium">
                                            Passwort
                                        </Label>
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotPassword(true)}
                                            className="text-xs text-primary transition-colors hover:text-primary/80"
                                        >
                                            Passwort vergessen?
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(inputEvent) => setPassword(inputEvent.target.value)}
                                            required
                                            className="h-12 rounded-xl bg-background/50 pl-10"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    variant="hero"
                                    size="lg"
                                    className="mt-6 w-full"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    ) : (
                                        <>
                                            Weiter mit Walter
                                            <ArrowRight className="h-5 w-5" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        ) : (
                            <RegisterForm
                                fieldIdPrefix="auth-register"
                                useEmailAsUsername
                                showInlineErrorMessage
                                onSuccess={(payload) => {
                                    router.replace(
                                        `/auth/registration-info?status=${isAdult(payload.age) ? 'adult' : 'parent'}`,
                                    )
                                }}
                            />
                        )}

                        <div className="mt-6 text-center">
                            <button
                                type="button"
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                                {isLogin ? (
                                    <span>
                                        Noch keinen Account? <br />
                                        <strong>Sichere deinen Fortschritt</strong>
                                    </span>
                                ) : (
                                    <span>
                                        Schon registriert? <strong>Melde dich an</strong>
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Benefits Teaser */}
                    {/* <div className="mt-8 lg:hidden">
                        <div className="flex items-center justify-center gap-6 text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-primary" />
                                <span className="text-xs">Verstehe deine Stärken</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-primary" />
                                <span className="text-xs">Entdecke passende Berufe</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-primary" />
                                <span className="text-xs">Finde Unternehmen in deiner Nähe</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-primary" />
                                <span className="text-xs">KI-gestützt</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-secondary" />
                                <span className="text-xs">Personalisiert</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-accent" />
                                <span className="text-xs">Gamification</span>
                            </div>
                        </div>
                    </div> */}

                    <div className="mt-2 pt-6 space-y-3">
                        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                                <Zap className="w-3.5 h-3.5 text-primary" />
                                <span className="text-xs">Verstehe deine Stärken</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Target className="w-3.5 h-3.5 text-green-500" />
                                <span>Entdecke passende Berufe</span>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <Building2 className="w-3.5 h-3.5 text-purple-500" />
                                <span className="text-xs">Finde passende Unternehmen</span>
                            </div>
                        </div>
                    </div>

                    {/* <div className="mt-2 pt-6 space-y-3 lg:hidden">
                        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground flex-col">
                            <div className="flex items-center gap-1.5">
                                <Zap className="w-3.5 h-3.5 text-primary" />
                                <span className="text-xs">Verstehe deine Stärken</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Target className="w-3.5 h-3.5 text-green-500" />
                                <span>Entdecke Berufe, die zu dir passen</span>
                            </div>
                            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-3.5 h-3.5 text-purple-500" />
                                    <span className="text-xs">Finde passende Unternehmen</span>
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Footer with DSGVO */}
                    <div className="mt-5 pt-6 border-t border-border/50 space-y-3">
                        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                                <Shield className="w-3.5 h-3.5 text-green-500" />
                                <span>100% DSGVO-konform</span>
                            </div>
                            <span className="text-border">•</span>
                            {/* <span>Made with ❤️ in Germany</span> */}
                            <span>Deine Daten gehören dir</span>
                        </div>
                        {/* <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                            <Link href="/imprint" className="hover:text-primary transition-colors">
                                Impressum
                            </Link>
                            <span className="text-border">•</span>
                            <Link href="/privace_data" className="hover:text-primary transition-colors">
                                Datenschutz
                            </Link>
                            <span className="text-border">•</span>
                            <Link href="/terms" className="hover:text-primary transition-colors">
                                AGB
                            </Link>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Forgot Password Dialog */}
            <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Passwort zurücksetzen</DialogTitle>
                        <DialogDescription>
                            Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="reset-email">E-Mail</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="reset-email"
                                    type="email"
                                    placeholder="deine@email.de"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={isResetting}>
                                {isResetting ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    'Link senden'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    )
}
